import { Activity, Bed, Ticket, ScrollText, Stethoscope, Receipt, FileSignature, MessageCircle, Pill, IdCard, Workflow, ShieldCheck, BarChart3, Boxes } from "lucide-react";

export const MODULES = [
  {
    id: "opd",
    name: "OPD",
    title: "Outpatient at the speed of thought",
    summary: "End-to-end OPD flow — registration, queue, consult, e-prescription.",
    icon: Stethoscope,
    image: "https://images.unsplash.com/photo-1666886573212-2de95596d509?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjB1c2luZyUyMGRpZ2l0YWwlMjB0YWJsZXR8ZW58MHx8fHwxNzc4MzcxMzM0fDA&ixlib=rb-4.1.0&q=85",
    bullets: ["Self-check-in kiosks", "Smart queue with token push", "Voice-ready e-prescriptions"],
  },
  {
    id: "ipd",
    name: "IPD",
    title: "Inpatient, end-to-end",
    summary: "Admissions, bed allocation, doctor rounds, nursing notes — one timeline.",
    icon: Bed,
    image: "https://images.pexels.com/photos/13697732/pexels-photo-13697732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bullets: ["Bed allocation board", "Doctor round notes", "Care-plan templates"],
  },
  {
    id: "token",
    name: "Token Mgmt",
    title: "Tokens that breathe with the floor",
    summary: "Live token boards across departments — synced with WhatsApp & SMS.",
    icon: Ticket,
    image: "https://images.pexels.com/photos/7108112/pexels-photo-7108112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bullets: ["Multi-counter routing", "WhatsApp token alerts", "Predictive wait time"],
  },
  {
    id: "ward",
    name: "Ward",
    title: "Wards, in command",
    summary: "Ward census, transfers, vitals — visible from every station.",
    icon: Boxes,
    image: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Live ward census", "Transfer + housekeeping", "Vitals & I/O charting"],
  },
  {
    id: "nurse",
    name: "Nurse Station",
    title: "A nurse's second brain",
    summary: "Med admin, vitals, alerts — fewer clicks, fewer errors.",
    icon: Activity,
    image: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Med-admin barcode", "Critical-value alerts", "Handover summaries"],
  },
  {
    id: "billing",
    name: "Billing",
    title: "Billing without bandages",
    summary: "Tariffs, packages, TPA, GST — ready for your audit, not against it.",
    icon: Receipt,
    image: "https://images.pexels.com/photos/3825586/pexels-photo-3825586.jpeg?auto=compress&cs=tinysrgb&w=1200",
    bullets: ["TPA + insurance flows", "Package / tariff engine", "GST + audit-ready"],
  },
  {
    id: "discharge",
    name: "Discharge",
    title: "Discharge in minutes, not hours",
    summary: "Auto-collected dues, signed summaries, prescription, follow-up.",
    icon: FileSignature,
    image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["One-click summary", "Pending-dues guardrail", "Follow-up scheduling"],
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    title: "WhatsApp, natively wired",
    summary: "Reports, reminders, follow-ups — on the channel patients already live in.",
    icon: MessageCircle,
    image: "https://images.unsplash.com/photo-1611605698335-8b1569810432?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Report delivery", "Appointment reminders", "Two-way templates"],
  },
  {
    id: "pharma",
    name: "Pharma Token",
    title: "Pharmacy that flows",
    summary: "Prescription → pharmacy queue → dispense, with stock guardrails.",
    icon: Pill,
    image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Token-linked dispensing", "Batch + expiry control", "Auto re-order"],
  },
  {
    id: "visitor",
    name: "Visitor Pass",
    title: "Visitors, calmly controlled",
    summary: "Digital passes, ward limits, audit logs — security without the chaos.",
    icon: IdCard,
    image: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["QR visitor passes", "Ward-wise limits", "Full audit trail"],
  },
  {
    id: "workflow",
    name: "Workflow",
    title: "Workflow, your way",
    summary: "Custom workflows for every hospital — without forking the product.",
    icon: Workflow,
    image: "https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Drag-and-drop builder", "Role-aware approvals", "Hospital-grade SLAs"],
  },
  {
    id: "analytics",
    name: "Analytics",
    title: "The hospital, on a single dashboard",
    summary: "Live ops board for occupancy, revenue, TAT, satisfaction.",
    icon: BarChart3,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=tinysrgb&w=1200&q=80",
    bullets: ["Live occupancy", "Revenue + collections", "TAT, NPS, FTR"],
  },
];

export const STATS = [
  { value: "42%", label: "Avg. drop in patient wait time" },
  { value: "27%", label: "Lower operating cost per bed" },
  { value: "3.1×", label: "Faster discharge cycle" },
  { value: "99.95%", label: "Uptime SLA" },
];

export const COMPLIANCE = [
  { name: "ABDM Compliant", note: "Built-in ABHA, HFR, HPR linkage" },
  { name: "CERT-IN Empaneled", note: "Audited by CERT-IN empaneled agency" },
  { name: "ISO 27001 Aligned", note: "Hardened access, audit, encryption" },
  { name: "DPDPA Ready", note: "Consent, retention, redaction" },
];

export const PRICING = [
  {
    id: "clinic",
    name: "Clinic",
    tagline: "For OPD-led clinics & polyclinics",
    price: "₹4,999",
    period: "/ month",
    cta: "Start with Clinic",
    features: [
      "OPD + Token + Billing",
      "WhatsApp report delivery",
      "Up to 5 users",
      "Email support",
    ],
  },
  {
    id: "hospital",
    name: "Hospital",
    tagline: "For 25-200 bed hospitals",
    price: "₹24,999",
    period: "/ month",
    cta: "Book a Hospital demo",
    featured: true,
    features: [
      "Everything in Clinic",
      "IPD, Ward, Nurse Station",
      "Pharma + Visitor Pass",
      "ABDM + Insurance/TPA",
      "Up to 50 users",
      "Priority onboarding",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Multi-unit, custom SLAs",
    price: "Let's talk",
    period: "",
    cta: "Talk to founder",
    features: [
      "Unlimited users + units",
      "Custom integrations & workflows",
      "Dedicated success manager",
      "On-prem / private cloud",
      "Custom SLA + DR",
    ],
  },
];

export const FAQS = [
  { q: "Is PranaOs ABDM compliant?", a: "Yes. ABHA, HFR and HPR flows are built in. We support FHIR R4 and the M1, M2 milestones." },
  { q: "How long does onboarding take?", a: "A standalone clinic is live in 7 days. A 100-bed hospital typically goes live in 4-6 weeks with parallel staff training." },
  { q: "Can you integrate with our LIS / RIS / accounting?", a: "Yes. We have HL7/FHIR adapters and a workflow studio for custom integrations — including Tally, SAP, LIS and PACS." },
  { q: "Is patient data secure?", a: "We are audited by a CERT-IN empaneled agency, encrypt data in transit and at rest, and ship a complete audit log." },
  { q: "Do you offer on-premise deployment?", a: "Enterprise customers can choose private cloud or on-prem with the same product surface." },
  { q: "What about WhatsApp delivery?", a: "We use approved business templates for reports, appointments and follow-ups — opt-in by design." },
];
